import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Loader2, RefreshCw, FileSpreadsheet, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { CbtScorecard } from './CbtScorecard';
import { CbtLeads } from './CbtLeads';
import { CbtTrend } from './CbtTrend';
import { CbtLostEarly } from './CbtLostEarly';
import { CbtSettings } from './CbtSettings';

const PERIODS = [['today', 'Today'], ['yesterday', 'Yesterday'], ['7d', 'Last 7 days'], ['month', 'This month'], ['custom', 'Custom']];
export const when = v => (v ? new Date(v).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

export default function CallbackTrackerPage() {
  const [sp] = useSearchParams();
  const [tab, setTab] = useState(sp.get('tab') || (sp.get('cold') ? 'leads' : 'scorecard'));
  useEffect(() => { if (sp.get('tab') || sp.get('cold')) setTab(sp.get('tab') || 'leads'); }, [sp]);
  const [period, setPeriod] = useState('today');
  const [range, setRange] = useState({ frm: '', to: '' });
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);
  const poll = useRef(null);
  const params = period === 'custom' ? { period, ...range } : { period };

  const load = useCallback(() => {
    if (period === 'custom' && !(range.frm && range.to)) return;
    api.get('/callback-tracker/report', { params: period === 'custom' ? { period, ...range } : { period } })
      .then(r => { setData(r.data); setRunning(!!r.data.state?.running); })
      .catch(e => toast.error(e.response?.data?.detail || 'Could not load'));
  }, [period, range]);
  useEffect(() => { load(); }, [load]);

  const watch = useCallback(() => {
    clearInterval(poll.current);
    poll.current = setInterval(async () => {
      const s = (await api.get('/callback-tracker/state')).data;
      if (!s.running) {
        clearInterval(poll.current); setRunning(false); load();
        s.last_error ? toast.error(`CRM read failed: ${s.last_error}`) : toast.success('Updated from Zoho CRM');
      }
    }, 5000);
  }, [load]);
  useEffect(() => () => clearInterval(poll.current), []);
  useEffect(() => { if (running) watch(); }, [running, watch]);

  const refresh = async () => {
    try { await api.post('/callback-tracker/refresh'); setRunning(true); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const exportXlsx = async () => {
    try {
      const r = await api.get('/callback-tracker/export', { params, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `callback-followup-${data.from}_${data.to}.xlsx`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch { toast.error('Export failed'); }
  };
  const st = data?.state || {};

  return (
    <div className="space-y-5" data-testid="callback-tracker-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Call Back Follow-up</h1>
          <p className="text-sm text-slate-500 mt-0.5">Calls logged in Zoho CRM on leads in Call Back · credited to the user who called · cold = not called for {data?.settings?.cold_days ?? 2}+ days · updates 9 AM & 11:59 PM IST</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={refresh} disabled={running} data-testid="cbt-refresh-btn">
            {running ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}{running ? 'Reading CRM…' : 'Update from CRM'}</Button>
          <Button size="sm" variant="outline" onClick={exportXlsx} disabled={!data} data-testid="cbt-export-btn"><FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />Export Excel</Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <div className="flex bg-slate-100 rounded-md p-0.5" data-testid="cbt-period">
          {PERIODS.map(([k, l]) => <button key={k} onClick={() => setPeriod(k)} className={`px-2.5 py-1 rounded ${period === k ? 'bg-white shadow-sm font-semibold text-slate-900' : 'text-slate-500'}`} data-testid={`cbt-period-${k}`}>{l}</button>)}
        </div>
        {period === 'custom' && <>
          <Input type="date" value={range.frm} onChange={e => setRange(r => ({ ...r, frm: e.target.value }))} className="h-8 w-36 text-xs" data-testid="cbt-from" />
          <Input type="date" value={range.to} onChange={e => setRange(r => ({ ...r, to: e.target.value }))} className="h-8 w-36 text-xs" data-testid="cbt-to" />
          {!(range.frm && range.to) && <span className="text-amber-700" data-testid="cbt-custom-hint">Pick both dates — showing {data?.from} → {data?.to} until then</span>}
        </>}
        <span className="text-slate-400" data-testid="cbt-refreshed-at">{st.refreshed_at ? `Last read ${when(st.refreshed_at)} · ${st.refreshed_by}` : 'Not read from Zoho CRM yet — click Update from CRM'}</span>
        <Tabs value={tab} onValueChange={setTab} className="ml-auto">
          <TabsList>
            <TabsTrigger value="scorecard" data-testid="cbt-tab-scorecard">User scorecard</TabsTrigger>
            <TabsTrigger value="leads" data-testid="cbt-tab-leads">Leads</TabsTrigger>
            <TabsTrigger value="trend" data-testid="cbt-tab-trend">Daily trend</TabsTrigger>
            <TabsTrigger value="lost" data-testid="cbt-tab-lost">Lost too early</TabsTrigger>
            <TabsTrigger value="settings" data-testid="cbt-tab-settings">Rules</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      {st.last_error && <p className="text-xs bg-rose-50 border border-rose-200 text-rose-800 rounded-md px-3 py-2 flex items-center gap-2" data-testid="cbt-error"><AlertTriangle className="w-3.5 h-3.5" />Last CRM read failed: {st.last_error}</p>}
      {!data && <div className="py-20 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>}
      {data && tab === 'scorecard' && <CbtScorecard data={data} />}
      {data && tab === 'leads' && <CbtLeads key={sp.get('cold') || 'x'} data={data} coldOnly={!!sp.get('cold')} />}
      {tab === 'trend' && <CbtTrend key={st.refreshed_at} />}
      {tab === 'lost' && <CbtLostEarly key={st.refreshed_at} />}
      {tab === 'settings' && <CbtSettings initial={data?.settings} onSaved={load} />}
    </div>
  );
}
